import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { IMarketplaceController } from "./IMarketplaceController";
import { IMarketplaceMapModel } from "../map/IMarketplaceMapModel";
import { MapFilter } from "../map/MapFilter";
import { IMarketplaceViewModel } from "../IMarketplaceViewModel";
import { MarketplaceCard } from "../../components/MarketplaceCard";
import { IMarketplaceBuildingModel } from "../building/IMarketplaceBuildingModel";
import { SceneManager } from "../../../../../../../scene/SceneManager";
import { IPlayerModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IPlayerModel";
import { IBenefitsModel } from "by/gamefactory/cityversetycoon/scenes/common/models/benefits/IBenefitsModel";
import { MarketplaceBuildingItem } from "../building/MarketplaceBuildingItem";
import { MyWalletModule } from "../../../../myWallet/MyWalletModule";
import { IContextResolver } from "../../../../../../composition/IContextResolver";
import { PaymentResult } from "../../../../../../../web3/PaymentResult";
import { ILogger } from "../../../../../../logger/ILogger";
import { AsyncOperationNotification } from "../../../../../common/components/AsyncOperationNotification";
import { IGameModel } from "../../../../../game/models/IGameModel";
import { MyDiscountPopupSceneFactory, PriceHistoryPopupSceneFactory, RemoveBuildingPopupSceneFactory, UseDiscountPopupSceneFactory } from "../../../MarketplaceFactories";
import { ShowRemoveSceneParams } from "../../../removebuilding/ShowRemoveSceneParams";
import { CancellationToken } from "async/CancellationToken";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { DefaultMessagesSceneFactory } from "by/gamefactory/cityversetycoon/scenes/SceneFactories";
import { CancellationTokenSource } from "async/CancellationTokenSource";
import { WalletTabType } from "../../../../myWallet/components/WalletTabType";
import { MarketplaceListItem } from "../map/MarketplaceListItem";
import { PaymentProcessingType } from "../../../../ethL1paymentprocessing/type/PaymentProcessingType";
import { EthL1PaymentProcessingModule } from "../../../../ethL1paymentprocessing/EthL1PaymentProcessingModule";
import { EthL1PaymentProcessingPopupSceneOptions } from "../../../../ethL1paymentprocessing/scenes/EthL1PaymentProcessingPopupSceneOptions";
import { PurchaseHistory } from "../building/PurchaseHistory";
import { ShowEditPriceSceneParams } from "../../../../editprice/scene/ShowEditPriceSceneParams";
import { EditPriceModule } from "../../../../editprice/EditPriceModule";
import { Erc20PaymentProcessingModule } from "../../../../ethL1paymentprocessing/Erc20PaymentProcessingModule";
import { PaymentProcessingPopupSceneOptions } from "../../../../ethL1paymentprocessing/scenes/PaymentProcessingPopupSceneOptions";
import { Web3Config } from "by/gamefactory/web3/config/Web3Config";

@injectable()
export class MarketplaceController implements IMarketplaceController {
    private readonly marketplaceModel: IMarketplaceMapModel;
    private readonly marketplaceViewModel: IMarketplaceViewModel;
    private readonly marketplaceBuildingModel: IMarketplaceBuildingModel;
    private readonly benefitsModel: IBenefitsModel;
    private readonly sceneManager: SceneManager;
    private readonly web3Model: Web3Model;
    private readonly web3Config: Web3Config;
    private readonly contextResolver: IContextResolver;
    private readonly logger: ILogger;
    private readonly playerModel: IPlayerModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly gameModel: IGameModel;

    public constructor(
        sceneManager: SceneManager,
        web3Model: Web3Model,
        web3Config: Web3Config,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("IMarketplaceMapModel") marketplaceModel: IMarketplaceMapModel,
        @inject("IMarketplaceViewModel") marketplaceViewModel: IMarketplaceViewModel,
        @inject("IMarketplaceBuildingModel") marketplaceBuildingModel: IMarketplaceBuildingModel,
        @inject("IBenefitsModel") benefitsModel: IBenefitsModel,
        @inject("IContextResolver") contextResolver: IContextResolver,
        @inject("ILogger") logger: ILogger,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IGameModel") gameModel: IGameModel
    ) {
        this.sceneManager = sceneManager;
        this.web3Model = web3Model;
        this.web3Config = web3Config;
        this.asyncOperationNotification = asyncOperationNotification;
        this.marketplaceModel = marketplaceModel;
        this.marketplaceViewModel = marketplaceViewModel;
        this.marketplaceBuildingModel = marketplaceBuildingModel;
        this.benefitsModel = benefitsModel;
        this.contextResolver = contextResolver;
        this.logger = logger;
        this.playerModel = playerModel;
        this.gameModel = gameModel;
        this.asyncOperationNotification = asyncOperationNotification;
    }

    @initializer()
    public async initialize(): Promise<void> {
        await this.updateMapFilter(new MapFilter(this.marketplaceViewModel.defaultSelectedZoneId.value));
    }

    public async updateAreas(): Promise<void> {
        await this.marketplaceModel.updateAreas(this.marketplaceViewModel.returnOnlyBuildingsByMon.value);
    }

    public async updateMapFilter(mapFilter: MapFilter, force: boolean = true): Promise<void> {
        mapFilter.returnOnlyBuildingsByMon = this.marketplaceViewModel.returnOnlyBuildingsByMon.value;
        mapFilter.orderByPriceInMon = this.marketplaceViewModel.orderByPriceInMon.value;
        this.marketplaceViewModel.currentMapFilter.setValue(mapFilter);
        await this.marketplaceModel.updateMapFilter(mapFilter, force);
    }

    public async updateMapFilterWithNextOffset(): Promise<void> {
        const currentMapFilter = this.marketplaceViewModel.currentMapFilter.value;
        const newMapFilter = new MapFilter(currentMapFilter.area, currentMapFilter.zipCode, currentMapFilter.limit, currentMapFilter.offset + currentMapFilter.limit, currentMapFilter.returnOnlyBuildingsByMon, this.marketplaceViewModel.orderByPriceInMon.value);
        this.marketplaceViewModel.currentMapFilter.setValue(newMapFilter);
        await this.marketplaceModel.updateMapFilter(newMapFilter, true);
    }

    public async updateSelectedMarketplaceCard(selectedCard: MarketplaceCard): Promise<boolean> {
        this.marketplaceViewModel.selectedMarketplaceCard.setValue(selectedCard);
        if (!await this.marketplaceBuildingModel.loadBuildingInfo(selectedCard.listItem.id)) {
            return false;
        }
        if (this.marketplaceBuildingModel.currentBuilding.value.building.supportCoupon) {
            await this.benefitsModel.getBenefits();
        }
        return true;
    }

    public async showEditPricePopup(
        buildingId: string,
        tokenId: string,
        orderId: string,
        currentPrice: number,
        purchaseHistory: Array<PurchaseHistory>,
        cancellationToken: CancellationToken
    ): Promise<boolean> {
        const result = await this.showEditPriceScene(buildingId, tokenId, orderId, currentPrice, purchaseHistory, cancellationToken);
        if (result) {
            await this.updateSelectedMarketplaceCard(this.marketplaceViewModel.selectedMarketplaceCard.value);
            const currentMapFilter = this.marketplaceViewModel.currentMapFilter.value;
            await this.updateMapFilter(new MapFilter(currentMapFilter.area, currentMapFilter.zipCode));
        }
        return result;
    }

    public async showRemoveBuildingPopup(buildingId: string, orderId: string, cancellationToken: CancellationToken): Promise<boolean> {
        const result = await this.showRemoveBuildingScene(buildingId, orderId, cancellationToken);
        if (result) {
            this.asyncOperationNotification.show();
            await this.marketplaceModel.updateAreas(this.marketplaceViewModel.returnOnlyBuildingsByMon.value);
            const currentMapFilter = this.marketplaceViewModel.currentMapFilter.value;
            await this.updateMapFilter(new MapFilter(currentMapFilter.area, undefined));
            this.asyncOperationNotification.hide();
        }
        return result;
    }

    public async paymentBuildingWithMint(buildingId: string, cancellationToken: CancellationToken, couponBenefitIds?: Array<string>): Promise<PaymentResult> {
        const cancellationTokenSource = new CancellationTokenSource(cancellationToken);
        const token = cancellationTokenSource.token;
        try {
            const paymentOperation = this.web3Model.paymentBuildingWithMint(buildingId, token, couponBenefitIds);
            return await this.showPaymentMessage(paymentOperation, token);
        }
        catch (e: unknown) {
            this.logger.error("Exception on process payment building with mint: ", e);
            return PaymentResult.FAILURE;
        }
        finally {
            cancellationTokenSource.dispose();
        }
    }

    public async paymentBuildingWithoutMint(tokenId: string, cancellationToken: CancellationToken): Promise<PaymentResult> {
        const cancellationTokenSource = new CancellationTokenSource(cancellationToken);
        const token = cancellationTokenSource.token;
        try {
            const paymentOperation = this.web3Model.paymentBuildingWithoutMint(tokenId, token);
            return await this.showPaymentMessage(paymentOperation, token);
        }
        catch (e: unknown) {
            this.logger.error("Exception on process payment building without mint: ", e);
            return PaymentResult.FAILURE;
        }
    }

    public async buyBuildingFromReservoirWithCrypto(orderId: string, cancellationToken: CancellationToken): Promise<PaymentResult> {
        const cancellationTokenSource = new CancellationTokenSource(cancellationToken);
        const token = cancellationTokenSource.token;
        try {
            const paymentOperation = this.web3Model.buyBuildingThroughReservoir(orderId);
            return await this.showPaymentMessage(paymentOperation, token);
        } catch (e: unknown) {
            this.logger.error("Exception on process payment building throu reservoir: ", e);
            return PaymentResult.FAILURE;
        } finally {
            cancellationTokenSource.dispose();
        }
    }

    public async showMyDiscountPopup(cancellationToken: CancellationToken): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            const sceneFactory: MyDiscountPopupSceneFactory = await this.contextResolver.resolve("MyDiscountPopupSceneFactory");
            const scene = await sceneFactory(cancellationToken);
            this.asyncOperationNotification.hide();
            await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error("Exception on showing MyDiscount: ", e);
        }
    }

    public async showSelectPaymentMethodPopup(currentMpBuildingItem: MarketplaceBuildingItem, cancellationToken: CancellationToken): Promise<PaymentResult> {
        try {
            if (!currentMpBuildingItem.building.supportCoupon) {
                if (currentMpBuildingItem.price.mon != null) {
                    return await this.showMonPaymentProcessingPopup(currentMpBuildingItem.id, currentMpBuildingItem.price.mon);
                } else {
                    return await this.showUsdcPaymentProcessingPopup(currentMpBuildingItem.id, currentMpBuildingItem.price.usd, currentMpBuildingItem.reservoirOrderId, currentMpBuildingItem.building.tokenId);
                }
            }
            if (this.benefitsModel.benefits.value?.length > 0) {
                return await this.showUseDiscountScene(currentMpBuildingItem, cancellationToken);
            }
            if (currentMpBuildingItem.price.mon != null) {
                return await this.showMonPaymentProcessingPopup(currentMpBuildingItem.id, currentMpBuildingItem.price.mon);
            } else {
                return await this.showUsdcPaymentProcessingPopup(currentMpBuildingItem.id, currentMpBuildingItem.price.usd, currentMpBuildingItem.reservoirOrderId, currentMpBuildingItem.building.tokenId);
            }
        } catch (e: unknown) {
            this.logger.error("Exception on show UseDiscountPopupWithCrypto: ", e);
            return PaymentResult.FAILURE;
        }
    }

    public async showUseDiscountPopup(currentMpBuildingItem: MarketplaceBuildingItem, cancellationToken: CancellationToken): Promise<PaymentResult> {
        try {
            if (!currentMpBuildingItem.building.supportCoupon) {
                if (currentMpBuildingItem.price.mon != null) {
                    return await this.showMonPaymentProcessingPopup(currentMpBuildingItem.id, currentMpBuildingItem.price.mon);
                } else {
                    return await this.paymentBuildingWithMint(currentMpBuildingItem.id, cancellationToken);
                }
            }
            if (this.benefitsModel.benefits.value?.length > 0) {
                return await this.showUseDiscountScene(currentMpBuildingItem, cancellationToken);
            }
            if (currentMpBuildingItem.price.mon != null) {
                return await this.showMonPaymentProcessingPopup(currentMpBuildingItem.id, currentMpBuildingItem.price.mon);
            } else {
                return await this.paymentBuildingWithMint(currentMpBuildingItem.id, cancellationToken);
            }
        }
        catch (e: unknown) {
            this.logger.error("Exception on show UseDiscountPopup: ", e);
            return PaymentResult.FAILURE;
        }
    }

    public async showMonPaymentProcessingPopup(buildingId: string, price: number): Promise<PaymentResult> {
        const monPaymentProcessingModule = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(EthL1PaymentProcessingModule));
        return await monPaymentProcessingModule.start({
            type: PaymentProcessingType.BUILDING,
            price,
            buildingId,
        } as EthL1PaymentProcessingPopupSceneOptions<PaymentProcessingType.STORE_PACKAGE>); // @giogos - check with Egor if this correct ...
    }

    public async showUsdcPaymentProcessingPopup(buildingId: string, price: number, reservoirOrderId: string, tokenId: string): Promise<PaymentResult> {
        try {
            this.asyncOperationNotification.show();
            const cryptoPaymentProcessingModule = await this.contextResolver.resolve(Erc20PaymentProcessingModule)
            const tokenSymbol = await this.web3Model.getTokenSymbol(this.web3Config.targetChain, this.web3Config.reservoirDevUsdcCurrency);
            this.asyncOperationNotification.hide();
            return await cryptoPaymentProcessingModule.start({
                chain: this.web3Config.targetChain,
                erc20Address: this.web3Config.reservoirDevUsdcCurrency,
                tokenSymbol, 
                type: PaymentProcessingType.BUILDING,
                price,
                buildingId,
                tokenId,
                reservoirOrderId,
            } as PaymentProcessingPopupSceneOptions<PaymentProcessingType.STORE_PACKAGE>);
        } catch(e: unknown) {
            this.logger.error("Exception processing usdc payment popup in marketplace: ", e);
            return PaymentResult.FAILURE;
        } finally {
            this.asyncOperationNotification.hide();
        }
    }

    public async showMyWalletPopup(): Promise<void> {
        const myWalletModule = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(MyWalletModule));
        await myWalletModule.start(WalletTabType.BUILDING);
    }

    public async updatePlayerDataAndMarketplace(building: MarketplaceListItem): Promise<boolean> {
        this.asyncOperationNotification.show();
        try {
            await this.playerModel.loadPlayerData();
            await this.playerModel.loadBuildings(false);
            await this.gameModel.getCurrentAction();

            await this.updateMapFilterForBuilding(building);
            return await this.updateSelectedMarketplaceCard(this.marketplaceViewModel.selectedMarketplaceCard.value);
        }
        catch (e: unknown) {
            this.logger.error("Exception on update player data and Marketplace card: ", e);
        }
        finally {
            this.asyncOperationNotification.hide();
        }
    }

    private async updateMapFilterForBuilding(building: MarketplaceListItem): Promise<boolean> {
        const currentFilter = this.marketplaceViewModel.currentMapFilter.value;

        let offset = 0;
        while (true) {
            const mapFilter = new MapFilter(currentFilter.area, currentFilter.zipCode, currentFilter.limit, offset);
            const previousBuildings = this.marketplaceModel.buildings.value;
            await this.updateMapFilter(mapFilter, true);
            if (previousBuildings.every((oldBuilding) => this.marketplaceModel.buildings.value.find((newBuilding) => oldBuilding.id === newBuilding.id) != null)) {
                return false;
            }
            for (const newBuilding of this.marketplaceModel.buildings.value) {
                if (newBuilding.id === building.id) {
                    return true;
                }
            }
            offset += currentFilter.limit;
        }
    }

    private async showPaymentMessage(operation: Promise<PaymentResult>, cancellationToken: CancellationToken): Promise<PaymentResult> {
        try {
            this.asyncOperationNotification.show();
            const factory: DefaultMessagesSceneFactory = await this.contextResolver.resolve("DefaultMessagesSceneFactory");
            const scene = await factory({ operation, operationCancellationToken: cancellationToken, withProcessingPage: true });
            this.asyncOperationNotification.hide();
            if (cancellationToken.isCancellationRequested) {
                scene.view.destroy();
                return PaymentResult.CANCELLED;
            }
            return await this.sceneManager.showViewableAsync(scene) as PaymentResult;
        }
        catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error("Exception on showing payment message: ", e);
            return PaymentResult.FAILURE;
        }
    }

    private async showRemoveBuildingScene(buildingId: string, orderID: string, cancellationToken: CancellationToken): Promise<boolean> {
        try {
            this.asyncOperationNotification.show();
            const sceneFactory: RemoveBuildingPopupSceneFactory = await this.contextResolver.resolve("RemoveBuildingPopupSceneFactory");
            const scene = await sceneFactory(new ShowRemoveSceneParams(buildingId, orderID, cancellationToken));
            this.asyncOperationNotification.hide();
            return await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error("Exception on showing RemoveBuilding: ", e);
            return false;
        }
    }

    private async showEditPriceScene(
        buildingId: string,
        tokenId: string,
        orderId: string,
        currentPrice: number,
        purchaseHistory: Array<PurchaseHistory>,
        cancellationToken: CancellationToken
    ): Promise<boolean> {
        try {
            const module = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(EditPriceModule));
            return await module.start(new ShowEditPriceSceneParams(buildingId, tokenId, currentPrice, false, orderId, purchaseHistory, cancellationToken));
        } catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error("Exception on showing EditPrice: ", e);
            return false;
        }
    }

    private async showUseDiscountScene(currentMpBuildingItem: MarketplaceBuildingItem, cancellationToken: CancellationToken): Promise<PaymentResult> {
        try {
            this.asyncOperationNotification.show();
            const sceneFactory: UseDiscountPopupSceneFactory = await this.contextResolver.resolve("UseDiscountPopupSceneFactory");
            const scene = await sceneFactory(currentMpBuildingItem, cancellationToken);
            this.asyncOperationNotification.hide();
            return await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error("Exception on showing UseDiscount: ", e);
            return PaymentResult.FAILURE;
        }
    }

    public async showPriceHistoryScene(priceHistory: Array<PurchaseHistory>): Promise<void> {
        try {
            const sceneFactory: PriceHistoryPopupSceneFactory = await this.contextResolver.resolve("PriceHistoryPopupSceneFactory");
            const scene = await sceneFactory(priceHistory);
            return await this.sceneManager.showViewableAsync(scene);
        } catch (e: unknown) {
            this.logger.error("Exception on showing PriceHistory: ", e);
        }
    }
}
